import { AvForm, AvRadioGroup, AvRadio } from "availity-reactstrap-validation"
import React, { useState, useEffect, useCallback } from "react"
import { Row, Col, Spinner, UncontrolledAlert } from "reactstrap"
import { Link, useParams } from "react-router-dom"
import api from "API/Api"
import SweetAlert from "react-bootstrap-sweetalert"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"

const VlccMobileSettings = () => {
  const { ccId } = useParams()
  const [settings, setSettings] = useState(null)
  const [pageLoad, setPageLoad] = useState(true)
  const [saving, setSaving] = useState({})
  const [lastSaved, setLastSaved] = useState("")
  const [resetConfirm, setResetConfirm] = useState(false)

  const fetchSettings = useCallback(async () => {
    try {
      const resp = await api.get("get-settings/" + ccId)
      setSettings(resp.data)
    } catch (e) { console.error("Failed to load settings:", e) }
    setPageLoad(false)
  }, [ccId])

  useEffect(() => { fetchSettings() }, [fetchSettings])

  const changeHandler = async (event, settingId, key) => {
    const val = event.target.value
    setSaving(prev => ({ ...prev, [key]: true }))
    try {
      await api.post("update-setting/" + settingId, { setting: val })
      setSettings(prev => ({ ...prev, [key]: val }))
      setLastSaved(key)
      setTimeout(() => setLastSaved(""), 2000)
    } catch (e) { console.error("Save failed:", e) }
    setSaving(prev => ({ ...prev, [key]: false }))
  }

  const changeTextHandler = async (event, settingId, key) => {
    const val = event.target.value
    setSettings(prev => ({ ...prev, [key]: val }))
    // Debounce: save after 800ms
    clearTimeout(window[`_debounce_${key}`])
    window[`_debounce_${key}`] = setTimeout(async () => {
      setSaving(prev => ({ ...prev, [key]: true }))
      try {
        await api.post("update-setting/" + settingId, { setting: val })
        setLastSaved(key)
        setTimeout(() => setLastSaved(""), 2000)
      } catch (e) { console.error(e) }
      setSaving(prev => ({ ...prev, [key]: false }))
    }, 800)
  }

  const handleReset = async () => {
    setPageLoad(true)
    try {
      const resp = await api.post("reset-settings/" + ccId)
      setSettings(resp.data)
    } catch (e) { console.error(e) }
    setPageLoad(false)
  }

  // Reusable card component for radio settings
  const SettingCard = ({ name, label, options, icon }) => {
    const isSaving = saving[name]
    const justSaved = lastSaved === name
    return (
      <div style={{
        background: "#fff", borderRadius: "10px", padding: "16px", marginBottom: "12px",
        border: justSaved ? "2px solid #22c55e" : "1px solid #e2e8f0",
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)", transition: "border-color 0.3s ease",
        position: "relative"
      }}>
        {isSaving && <div style={{ position: "absolute", top: 8, right: 8 }}><Spinner size="sm" color="primary" /></div>}
        {justSaved && <div style={{ position: "absolute", top: 8, right: 8, color: "#22c55e", fontSize: "12px", fontWeight: 600 }}>✓ Saved</div>}
        <label style={{ fontWeight: 600, fontSize: "13px", color: "#334155", marginBottom: "10px", display: "block" }}>
          {icon && <i className={`fas ${icon}`} style={{ marginRight: "6px", opacity: 0.6 }}></i>}
          {label}
        </label>
        <AvRadioGroup inline name={name} value={settings?.[name] || ""} onChange={e => changeHandler(e, settings?.[name + "_setting_id"], name)}>
          {options.map((opt, i) => <AvRadio key={i} label={opt.label} value={opt.value} />)}
        </AvRadioGroup>
      </div>
    )
  }

  // Reusable text input card
  const TextCard = ({ name, label, placeholder, type, icon }) => {
    const justSaved = lastSaved === name
    return (
      <div style={{
        background: "#fff", borderRadius: "10px", padding: "16px", marginBottom: "12px",
        border: justSaved ? "2px solid #22c55e" : "1px solid #e2e8f0",
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)", transition: "border-color 0.3s ease"
      }}>
        <label style={{ fontWeight: 600, fontSize: "13px", color: "#334155", marginBottom: "8px", display: "block" }}>
          {icon && <i className={`fas ${icon}`} style={{ marginRight: "6px", opacity: 0.6 }}></i>}
          {label}
          {justSaved && <span style={{ color: "#22c55e", fontSize: "11px", marginLeft: "8px" }}>✓ Saved</span>}
        </label>
        <input type={type || "text"} className="form-control" placeholder={placeholder || ""}
          value={settings?.[name] || ""} onChange={e => changeTextHandler(e, settings?.[name + "_setting_id"], name)} />
      </div>
    )
  }

  const SectionHeader = ({ icon, title, color, description }) => (
    <div style={{
      padding: "14px 18px", marginBottom: "16px", marginTop: "20px", borderRadius: "10px",
      background: `linear-gradient(135deg, ${color}12, ${color}08)`,
      borderLeft: `4px solid ${color}`
    }}>
      <h5 style={{ margin: 0, color, fontWeight: 700, fontSize: "16px" }}>
        <i className={`fas ${icon}`} style={{ marginRight: "8px" }}></i>{title}
      </h5>
      {description && <p style={{ margin: "4px 0 0", fontSize: "12px", color: "#64748b" }}>{description}</p>}
    </div>
  )

  const yesNo = [{ label: "Yes", value: "1" }, { label: "No", value: "2" }]

  // Drag-and-drop dual list for Export/Print parameters
  const DualListDnD = ({ name, label, allItems }) => {
    const [filter1, setFilter1] = useState("")
    const [filter2, setFilter2] = useState("")
    let selected = []
    try { selected = JSON.parse(settings?.[name] || "[]") } catch { selected = [] }
    const available = allItems.filter(i => !selected.includes(i))
    const filteredAvail = available.filter(i => i.toLowerCase().includes(filter1.toLowerCase()))
    const filteredSel = selected.filter(i => i.toLowerCase().includes(filter2.toLowerCase()))

    const save = (newSelected) => {
      const val = JSON.stringify(newSelected)
      const settingId = settings?.[name + "_setting_id"]
      setSettings(prev => ({ ...prev, [name]: val }))
      if (settingId) {
        setSaving(prev => ({ ...prev, [name]: true }))
        api.post("update-setting/" + settingId, { setting: val }).then(() => {
          setLastSaved(name)
          setTimeout(() => setLastSaved(""), 2000)
        }).catch(e => console.error(e)).finally(() => setSaving(prev => ({ ...prev, [name]: false })))
      }
    }

    const addItem = (item) => save([...selected, item])
    const removeItem = (item) => save(selected.filter(i => i !== item))

    const onDragEnd = (result) => {
      if (!result.destination) return
      const items = Array.from(selected)
      const [reordered] = items.splice(result.source.index, 1)
      items.splice(result.destination.index, 0, reordered)
      save(items)
    }

    const justSaved = lastSaved === name
    const isSaving = saving[name]
    const itemStyle = { padding: "8px 12px", marginBottom: "4px", background: "#6b7280", color: "#fff", borderRadius: "4px", cursor: "pointer", fontSize: "13px", display: "flex", justifyContent: "space-between", alignItems: "center" }

    return (
      <div style={{ background: "#fff", borderRadius: "10px", padding: "16px", marginBottom: "12px", border: justSaved ? "2px solid #22c55e" : "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)", position: "relative" }}>
        {isSaving && <div style={{ position: "absolute", top: 8, right: 8 }}><Spinner size="sm" color="primary" /></div>}
        {justSaved && <div style={{ position: "absolute", top: 8, right: 8, color: "#22c55e", fontSize: "12px", fontWeight: 600 }}>✓ Saved</div>}
        <label style={{ fontWeight: 600, fontSize: "14px", color: "#334155", marginBottom: "12px", display: "block" }}>
          <i className="fas fa-grip-vertical" style={{ marginRight: "6px", opacity: 0.6 }}></i>{label}
        </label>
        <Row>
          <Col md={6}>
            <div style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px" }}>Available</div>
            <input type="text" className="form-control form-control-sm mb-2" placeholder="Input Filter" value={filter1} onChange={e => setFilter1(e.target.value)} style={{ fontStyle: "italic" }} />
            <div style={{ maxHeight: "280px", overflowY: "auto" }}>
              {filteredAvail.map((item, i) => (
                <div key={i} style={itemStyle} onClick={() => addItem(item)}>
                  {item} <i className="fas fa-plus" style={{ fontSize: "10px" }}></i>
                </div>
              ))}
            </div>
          </Col>
          <Col md={6}>
            <div style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px" }}>Selected (Drag to reorder)</div>
            <input type="text" className="form-control form-control-sm mb-2" placeholder="Input Filter" value={filter2} onChange={e => setFilter2(e.target.value)} style={{ fontStyle: "italic" }} />
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId={name}>
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps} style={{ maxHeight: "280px", overflowY: "auto", minHeight: "40px" }}>
                    {filteredSel.map((item, index) => (
                      <Draggable key={item} draggableId={item} index={index}>
                        {(prov, snap) => (
                          <div ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps}
                            style={{ ...itemStyle, background: snap.isDragging ? "#4b5563" : "#6b7280", ...prov.draggableProps.style }}>
                            <span><i className="fas fa-grip-vertical" style={{ marginRight: "8px", opacity: 0.5 }}></i>{item}</span>
                            <i className="fas fa-times" style={{ fontSize: "10px", cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); removeItem(item) }}></i>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Col>
        </Row>
      </div>
    )
  }

  if (pageLoad) return (
    <div className="dashBody">
      <div className="text-center py-5">
        <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
        <p className="mt-3 text-muted">Loading Mobile Settings...</p>
      </div>
    </div>
  )

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader">
        <Row>
          <Col md={6}>
            <h4>
              <i className="fas fa-mobile-alt" style={{ marginRight: "8px", color: "#6366f1" }}></i>
              VLCC Mobile Settings
              {settings?.cc_name && <span style={{ fontSize: "14px", color: "#6c757d", marginLeft: "10px" }}>— {settings.cc_name} {settings.cc_number ? `(${settings.cc_number})` : ""}</span>}
            </h4>
          </Col>
          <Col md={6} style={{ textAlign: "right" }}>
            <button className="btn btn-outline-warning btn-sm me-2" onClick={() => setResetConfirm(true)} title="Reset to defaults">
              <i className="fas fa-undo"></i> Reset
            </button>
            <Link className="dashBtn" to="/collection-center-vlcc">
              <i className="fas fa-chevron-left"></i> <b>Back</b>
            </Link>
          </Col>
        </Row>
      </div>

      <div style={{ padding: "0 8px" }}>
        <AvForm className="form-horizontal">

          {/* ═══════════ PURCHASE SETTINGS ═══════════ */}
          <SectionHeader icon="fa-shopping-cart" title="Purchase Settings" color="#6366f1"
            description="Configure milk purchase entry, analyzer, and weighing scale behavior" />
          <Row>
            <Col md={4}>
              <SettingCard name="rate_chart_type" label="Rate Chart Type" icon="fa-chart-bar"
                options={[{ label: "Common For Both Milk", value: "1" }, { label: "Separate For Each Milk", value: "2" }]} />
            </Col>
            <Col md={4}>
              <SettingCard name="entry_mode" label="Entry Mode" icon="fa-keyboard"
                options={[{ label: "SNF", value: "1" }, { label: "CLR", value: "2" }]} />
            </Col>
            <Col md={4}>
              <SettingCard name="producer_input" label="Producer Input" icon="fa-user"
                options={[{ label: "Type Producer Number", value: "1" }, { label: "Choose From List", value: "2" }]} />
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <SettingCard name="excess_decimal" label="Excess Decimal" icon="fa-calculator"
                options={[{ label: "Round Off", value: "1" }, { label: "Truncate", value: "2" }]} />
            </Col>
            <Col md={4}>
              <SettingCard name="weighing_scale_type" label="Weighing Scale Type" icon="fa-weight"
                options={[{ label: "Single", value: "1" }, { label: "Continuous", value: "2" }]} />
            </Col>
            <Col md={4}>
              <SettingCard name="default_milk_type" label="Default Milk Type" icon="fa-tint"
                options={[{ label: "Cow", value: "1" }, { label: "Buffalo", value: "2" }, { label: "None", value: "0" }]} />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <SettingCard name="analyzer" label="Milk Analyzer" icon="fa-flask"
                options={[
                  { label: "Eko-One Time", value: "1" }, { label: "Eko-Continuous", value: "2" },
                  { label: "Essae", value: "3" }, { label: "Indiz", value: "4" },
                  { label: "KSheera", value: "5" }, { label: "LactoScan-9600", value: "6" },
                  { label: "Laktan-240", value: "7" }, { label: "Lacto Aavin", value: "8" },
                  { label: "LactoScan-2400", value: "9" }, { label: "Indifoss", value: "10" },
                  { label: "Real EMT", value: "11" }, { label: "Exor (Milk-O-Meter)", value: "12" },
                ]} />
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <SettingCard name="weighing_scale_factor" label="Weighing Scale Factor" icon="fa-ruler"
                options={[
                  { label: "Liter", value: "1" }, { label: "Deci Liter", value: "2" },
                  { label: "Centi Liter", value: "3" }, { label: "Milli Liter", value: "4" },
                  { label: "1/10 Milli Liter", value: "5" },
                ]} />
            </Col>
            <Col md={4}>
              <SettingCard name="quantity_decimal_precision" label="Quantity Decimal Precision" icon="fa-sort-numeric-up"
                options={[{ label: "0", value: "1" }, { label: "1", value: "2" }, { label: "2", value: "3" }]} />
            </Col>
            <Col md={4}>
              <SettingCard name="fat_decimal_precision" label="FAT Decimal Precision" icon="fa-sort-numeric-up"
                options={[{ label: "0", value: "1" }, { label: "1", value: "2" }, { label: "2", value: "3" }]} />
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <SettingCard name="snf_decimal_precision" label="SNF Decimal Precision" icon="fa-sort-numeric-up"
                options={[{ label: "0", value: "1" }, { label: "1", value: "2" }, { label: "2", value: "3" }]} />
            </Col>
            <Col md={4}>
              <SettingCard name="clr_decimal_precision" label="CLR Decimal Precision" icon="fa-sort-numeric-up"
                options={[{ label: "0", value: "1" }, { label: "1", value: "2" }, { label: "2", value: "3" }]} />
            </Col>
            <Col md={4}>
              <SettingCard name="weighing_tare_type" label="Weighing Tare Type" icon="fa-weight-hanging"
                options={[{ label: "Character", value: "1" }, { label: "ASCII", value: "2" }]} />
            </Col>
          </Row>

          {/* Toggle Settings Grid */}
          <div style={{
            background: "#f8fafc", borderRadius: "10px", padding: "16px", marginBottom: "16px",
            border: "1px solid #e2e8f0"
          }}>
            <h6 style={{ fontWeight: 700, color: "#475569", marginBottom: "12px" }}>
              <i className="fas fa-toggle-on" style={{ marginRight: "6px" }}></i> Quick Toggle Settings
            </h6>
            <Row>
              <Col md={4}><SettingCard name="can_weight_reduction" label="Can Weight Reduction" options={yesNo} /></Col>
              <Col md={4}><SettingCard name="weighing_scale_auto_tear" label="Weighing Scale Auto Tear" options={yesNo} /></Col>
              <Col md={4}><SettingCard name="auto_save_purchase" label="Auto Save Purchase" options={yesNo} /></Col>
            </Row>
            <Row>
              <Col md={4}><SettingCard name="remote_display_unit" label="Remote Display Unit" options={yesNo} /></Col>
              <Col md={4}><SettingCard name="auto_create_producer" label="Auto Create Producer" options={yesNo} /></Col>
              <Col md={4}><SettingCard name="can_entry" label="Can Entry" options={yesNo} /></Col>
            </Row>
            <Row>
              <Col md={4}><SettingCard name="testing_avg_entry" label="Testing Average Entry" options={yesNo} /></Col>
              <Col md={4}><SettingCard name="allow_other_session_entry" label="Allow Other Session Entry" options={yesNo} /></Col>
              <Col md={4}><SettingCard name="allow_quantity_input" label="Allow Quantity Input" options={yesNo} /></Col>
            </Row>
            <Row>
              <Col md={4}><SettingCard name="allow_manual_fat_input" label="Allow Manual FAT Input" options={yesNo} /></Col>
              <Col md={4}><SettingCard name="allow_manual_snf_clr_input" label="Allow Manual SNF/CLR Input" options={yesNo} /></Col>
              <Col md={4}><SettingCard name="allow_navigation" label="Allow Navigation" options={yesNo} /></Col>
            </Row>
            <Row>
              <Col md={4}><SettingCard name="allow_water" label="Allow Water %" options={yesNo} /></Col>
              <Col md={4}><SettingCard name="limit_single_entry" label="Limit Single Entry by Milk Type" options={yesNo} /></Col>
              <Col md={4}><SettingCard name="lock_fat_snf" label="Lock FAT & SNF After First Values" options={yesNo} /></Col>
            </Row>
            <Row>
              <Col md={4}><SettingCard name="make_analyzer_clean" label="Mandatory Analyzer Cleaning" options={yesNo} /></Col>
              <Col md={4}><SettingCard name="show_capture_button" label="Show Capture Button" options={yesNo} /></Col>
              <Col md={4}><SettingCard name="allow_zero_rate" label="Allow Zero Rate" options={yesNo} /></Col>
            </Row>
            <Row>
              <Col md={4}><SettingCard name="restrict_session_time" label="Restrict Session Time" options={yesNo} /></Col>
            </Row>
          </div>

          {/* ═══════════ BILLING & SCALE CONFIGURATION ═══════════ */}
          <SectionHeader icon="fa-balance-scale" title="Billing & Scale Configuration" color="#059669"
            description="Billing type, weighing scale communication, tear settings, and unit configuration" />
          <Row>
            <Col md={6}><SettingCard name="billing_type" label="Billing Type" icon="fa-receipt"
              options={[{ label: "Producer Billing", value: "1" }, { label: "Agent Billing", value: "2" }]} /></Col>
            <Col md={6}><SettingCard name="scale_incoming_unit_type" label="Weighing Scale Incoming Unit" icon="fa-weight"
              options={[{ label: "Quantity (Liters)", value: "1" }, { label: "Weight (Kg)", value: "2" }]} /></Col>
          </Row>
          <Row>
            <Col md={4}><SettingCard name="purchase_cow_milk_in" label="Purchase Cow Milk In" icon="fa-tint"
              options={[{ label: "Liters", value: "1" }, { label: "KG", value: "2" }]} /></Col>
            <Col md={4}><SettingCard name="purchase_buffalo_milk_in" label="Purchase Buffalo Milk In" icon="fa-tint"
              options={[{ label: "Liters", value: "1" }, { label: "KG", value: "2" }]} /></Col>
            <Col md={4}><SettingCard name="validate_scale_unit" label="Validate Weighing Scale Unit" options={yesNo} /></Col>
          </Row>
          <Row>
            <Col md={4}><SettingCard name="validate_quantity_twice" label="Validate Quantity Twice" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="secondary_qty_decimal_precision" label="Secondary Qty Decimal Precision"
              options={[{ label: "0", value: "1" }, { label: "1", value: "2" }, { label: "2", value: "3" }]} /></Col>
            <Col md={4}><SettingCard name="secondary_qty_excess_decimal" label="Secondary Qty Excess Decimal"
              options={[{ label: "Round Off", value: "1" }, { label: "Truncate", value: "2" }]} /></Col>
          </Row>
          <Row>
            <Col md={4}><SettingCard name="scale_baud_rate" label="Weighing Scale Baud Rate"
              options={[{ label: "2400", value: "2400" }, { label: "9600", value: "9600" }]} /></Col>
            <Col md={4}><TextCard name="scale_timeout" label="Weighing Scale Timeout (ms)" placeholder="e.g., 1000" icon="fa-clock" /></Col>
            <Col md={4}><TextCard name="scale_data_delimiter" label="Scale Data Delimiter" placeholder="e.g., 13" icon="fa-code" /></Col>
          </Row>
          <Row>
            <Col md={4}><SettingCard name="scale_tear_support" label="Scale Has Tear Support?" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="scale_tear_data_type" label="Scale Tear Data Type"
              options={[{ label: "Character", value: "1" }, { label: "ASCII", value: "2" }]} /></Col>
            <Col md={4}><TextCard name="scale_tear_data" label="Scale Tear Data" placeholder="e.g., T" icon="fa-terminal" /></Col>
          </Row>
          <Row>
            <Col md={4}><SettingCard name="scale_send_tear_twice" label="Send Tear Command Twice" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="show_tear_button" label="Show Tear Button in Purchase" options={yesNo} /></Col>
          </Row>

          {/* ═══════════ PRODUCER MANAGEMENT ═══════════ */}
          <SectionHeader icon="fa-users-cog" title="Producer Management" color="#7c3aed"
            description="Producer creation, numbering, auto-deactivation, and payment settings" />
          <Row>
            <Col md={4}><SettingCard name="purchase_by_short_number" label="Purchase By Short Number" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="allow_producer_add" label="Allow Producer Add" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="allow_producer_edit" label="Allow Producer Edit" options={yesNo} /></Col>
          </Row>
          <Row>
            <Col md={4}><SettingCard name="allow_alphabets_producer_number" label="Allow Alphabets in Producer No." options={yesNo} /></Col>
            <Col md={4}><SettingCard name="auto_assign_producer_number" label="Auto Assign Producer Number" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="auto_deactivate_absent_producer" label="Auto De-activate Absent Producers" options={yesNo} /></Col>
          </Row>
          <Row>
            <Col md={4}><TextCard name="deactivate_absent_days" label="Days to De-activate if Absent" placeholder="e.g., 30" icon="fa-calendar-times" /></Col>
            <Col md={4}><SettingCard name="allow_only_account_payment" label="Allow Only Account Payment" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="unique_account_number" label="Unique Account Number Per Producer" options={yesNo} /></Col>
          </Row>
          <Row>
            <Col md={4}><SettingCard name="producer_aadhaar_required" label="Producer Aadhaar Required" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="snf_clr_formula" label="SNF/CLR Calculation Formula" icon="fa-calculator"
              options={[
                { label: "Tamil Nadu", value: "1" }, { label: "Andhra Pradesh", value: "2" },
                { label: "Karnataka", value: "3" }, { label: "Maharashtra", value: "4" },
                { label: "Gujarat", value: "5" }, { label: "Rajasthan", value: "6" },
              ]} /></Col>
          </Row>

          {/* ═══════════ ADVANCED PURCHASE CONTROLS ═══════════ */}
          <SectionHeader icon="fa-sliders-h" title="Advanced Purchase Controls" color="#dc2626"
            description="Fine-grained purchase entry controls, validation, and session management" />
          <Row>
            <Col md={4}><SettingCard name="fat_excess_decimal" label="FAT Excess Decimal"
              options={[{ label: "Round Off", value: "1" }, { label: "Truncate", value: "2" }]} /></Col>
            <Col md={4}><SettingCard name="snf_excess_decimal" label="SNF Excess Decimal"
              options={[{ label: "Round Off", value: "1" }, { label: "Truncate", value: "2" }]} /></Col>
            <Col md={4}><SettingCard name="clr_excess_decimal" label="CLR Excess Decimal"
              options={[{ label: "Round Off", value: "1" }, { label: "Truncate", value: "2" }]} /></Col>
          </Row>
          <Row>
            <Col md={4}><SettingCard name="operation_excess_decimal" label="Operation Excess Decimal"
              options={[{ label: "Round Off", value: "1" }, { label: "Truncate", value: "2" }]} /></Col>
            <Col md={4}><SettingCard name="quantity_input_mode" label="Input Mode for Quantity" icon="fa-keyboard"
              options={[{ label: "Manual Only", value: "1" }, { label: "Auto Only", value: "2" }, { label: "Both", value: "3" }]} /></Col>
            <Col md={4}><TextCard name="quantity_alert_threshold" label="Alert When Quantity >" placeholder="e.g., 200" icon="fa-exclamation-triangle" /></Col>
          </Row>
          <Row>
            <Col md={4}><SettingCard name="allow_manual_fat_input" label="Allow Manual FAT Input" icon="fa-pen"
              options={[{ label: "Yes", value: "1" }, { label: "No", value: "2" }, { label: "For CM Only", value: "3" }, { label: "For BM Only", value: "4" }]} /></Col>
            <Col md={4}><SettingCard name="allow_manual_snf_clr_input" label="Allow Manual SNF/CLR Input" icon="fa-pen"
              options={[{ label: "Yes", value: "1" }, { label: "No", value: "2" }, { label: "For CM Only", value: "3" }, { label: "For BM Only", value: "4" }]} /></Col>
            <Col md={4}><SettingCard name="confirm_producer_before_purchase" label="Confirm Producer Before Purchase" options={yesNo} /></Col>
          </Row>
          <Row>
            <Col md={4}><SettingCard name="allow_save_without_fat_snf" label="Allow Save Without FAT & SNF" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="validate_min_max_fat" label="Validate Min/Max FAT Values" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="validate_min_max_snf" label="Validate Min/Max SNF Values" options={yesNo} /></Col>
          </Row>
          {settings?.validate_min_max_fat === "1" && (
            <Row>
              <Col md={4}><TextCard name="min_fat_value" label="Minimum FAT Value" placeholder="e.g. 2.5" icon="fa-arrow-down" /></Col>
              <Col md={4}><TextCard name="max_fat_value" label="Maximum FAT Value" placeholder="e.g. 10.0" icon="fa-arrow-up" /></Col>
            </Row>
          )}
          {settings?.validate_min_max_snf === "1" && (
            <Row>
              <Col md={4}><TextCard name="min_snf_value" label="Minimum SNF Value" placeholder="e.g. 7.0" icon="fa-arrow-down" /></Col>
              <Col md={4}><TextCard name="max_snf_value" label="Maximum SNF Value" placeholder="e.g. 11.0" icon="fa-arrow-up" /></Col>
            </Row>
          )}
          <Row>
            <Col md={4}><SettingCard name="auto_select_milk_by_fat" label="Auto Select Milk By FAT" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="restrict_viewing_other_sessions" label="Restrict Viewing Other Sessions" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="new_session_only_if_zero_unsynced" label="New Session Only If Zero Unsynced" options={yesNo} /></Col>
          </Row>
          <Row>
            <Col md={4}><SettingCard name="mobile_data_required" label="Mobile Data Must Be ON" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="limit_single_entry_mode" label="Limit Single Entry for Producer"
              options={[{ label: "Limit Single", value: "1" }, { label: "One Per Milk Type", value: "2" }, { label: "No Limit", value: "3" }]} /></Col>
            <Col md={4}><SettingCard name="show_average_summary" label="Show Average / Summary Print"
              options={[{ label: "Always", value: "1" }, { label: "After End Cleaning", value: "2" }, { label: "After End Session", value: "3" }]} /></Col>
          </Row>
          <Row>
            <Col md={4}><SettingCard name="sample_test_mandatory" label="Sample Test Mandatory at Session End" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="allow_material_add" label="Allow Material Add in App" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="quantity_deviation_alert" label="Alert on Quantity Deviation from Avg" options={yesNo} /></Col>
          </Row>
          <Row>
            <Col md={4}><SettingCard name="allow_local_sales" label="Allow Local Sales" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="enable_intelligent_charging" label="Enable Intelligent Charging" options={yesNo} /></Col>
          </Row>
          <Row>
            <Col md={4}><TextCard name="charging_start_percent" label="Start Charging At %" placeholder="e.g., 30" icon="fa-battery-quarter" /></Col>
            <Col md={4}><TextCard name="charging_stop_percent" label="Stop Charging At %" placeholder="e.g., 80" icon="fa-battery-full" /></Col>
          </Row>

          {/* ═══════════ PRINT SETTINGS ═══════════ */}
          <SectionHeader icon="fa-print" title="Print Settings" color="#0ea5e9"
            description="Configure receipt printing, header/footer text, and printer type" />
          <Row>
            <Col md={4}><TextCard name="print_header_text" label="Print Header Text" placeholder="Enter header text" icon="fa-heading" /></Col>
            <Col md={4}><SettingCard name="print_header" label="Show Print Header" options={yesNo} /></Col>
            <Col md={4}>
              <SettingCard name="printer_type" label="Printer Type" icon="fa-print"
                options={[
                  { label: "Always Ask", value: "1" }, { label: "2\" Thermal", value: "2" },
                  { label: "Built-in Thermal", value: "3" }, { label: "Text File", value: "4" }
                ]} />
            </Col>
          </Row>
          <Row>
            <Col md={4}><TextCard name="print_footer_text" label="Print Footer Text" placeholder="Enter footer text" icon="fa-shoe-prints" /></Col>
            <Col md={4}><SettingCard name="print_footer" label="Show Print Footer" options={yesNo} /></Col>
            <Col md={4}>
              <SettingCard name="auto_print" label="Auto Print After Purchase" icon="fa-print"
                options={[{ label: "Yes", value: "1" }, { label: "No", value: "2" }, { label: "Producer Wise", value: "3" }]} />
            </Col>
          </Row>
          <Row>
            <Col md={4}><SettingCard name="full_size_printer" label="Full Size Printer Available" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="summary_print_avg_position" label="Summary Print Avg Position"
              options={[{ label: "Top", value: "1" }, { label: "Bottom", value: "2" }]} /></Col>
            <Col md={4}><SettingCard name="summary_print_entry_time" label="Summary Print Entry Time" options={yesNo} /></Col>
          </Row>
          <Row>
            <Col md={4}><SettingCard name="show_unbilled_amount" label="Show Unbilled Amount in Receipt" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="announce_purchase_params" label="Announce Purchase Params" options={yesNo} /></Col>
          </Row>
          <DualListDnD name="print_parameters" label="Print Parameters" allItems={[
            "Date & Shift", "Producer Number", "Producer Name", "Milk Type", "Quantity", "FAT", "SNF",
            "CLR", "Water %", "Rate", "Total Amount", "Incentive", "Incentive Per Liter", "Purchase Time",
            "Producer Short Number", "Net Rate (Rate+Inc)", "Total Amount W/O Incentive"
          ]} />

          {/* ═══════════ SMS SETTINGS ═══════════ */}
          <SectionHeader icon="fa-sms" title="SMS Settings" color="#22c55e"
            description="Configure SMS notifications for purchases and security alerts" />
          <Row>
            <Col md={4}>
              <SettingCard name="auto_sms" label="Auto SMS After Purchase" icon="fa-comment-sms"
                options={[{ label: "Yes", value: "1" }, { label: "No", value: "2" }, { label: "Producer Wise", value: "3" }]} />
            </Col>
            <Col md={4}><TextCard name="security_branch_number" label="Security Breach Mobile" placeholder="Enter mobile number" icon="fa-shield-alt" /></Col>
            <Col md={4}><SettingCard name="security_branch_sms" label="Security Branch SMS" options={yesNo} /></Col>
          </Row>
          <Row>
            <Col md={4}><SettingCard name="sms_during_export" label="Summary SMS During Export" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="sms_header" label="SMS Header" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="sms_footer" label="SMS Footer" options={yesNo} /></Col>
          </Row>
          <Row>
            <Col md={4}><SettingCard name="send_summary_sms_session_close" label="Send Summary SMS During Session Close" options={yesNo} /></Col>
          </Row>

          {/* ═══════════ EXPORT SETTINGS ═══════════ */}
          <SectionHeader icon="fa-file-export" title="Export Settings" color="#f59e0b"
            description="Configure data export behavior and milk type labels" />
          <Row>
            <Col md={4}><SettingCard name="lock_purchase" label="Lock If Not Exported" options={yesNo} icon="fa-lock" /></Col>
            <Col md={4}><SettingCard name="export_producers" label="Export Producers With Purchase" options={yesNo} icon="fa-users" /></Col>
            <Col md={4}><TextCard name="cow_milk_export_text" label="Cow Milk Export Label" placeholder="e.g., Cow" icon="fa-tag" /></Col>
          </Row>
          <Row>
            <Col md={4}><TextCard name="buffalo_milk_export_text" label="Buffalo Milk Export Label" placeholder="e.g., Buffalo" icon="fa-tag" /></Col>
          </Row>
          <Row>
            <Col md={4}><SettingCard name="auto_export_session_close" label="Auto Export During Session Close" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="include_column_headers_ftp" label="Include Column Headers in FTP Export" options={yesNo} /></Col>
          </Row>
          <DualListDnD name="export_parameters" label="Export Parameters" allItems={[
            "Date", "Shift", "Serial Number", "Producer Number", "Milk Type", "Received Quantity (Liters)",
            "FAT", "SNF", "CLR", "Rate", "Incentive", "Total Amount", "Liters Entry Mode", "FAT Entry Mode",
            "SNF Entry Mode", "CLR Entry Mode", "Center Number", "Route Number", "Region Number", "Cans",
            "Temperature", "Purchase Insert Time", "Purchase Last Update Time", "Producer Name",
            "Center Name", "Route Name", "Chilling Center Name", "Water %", "Quantity in KG"
          ]} />

          {/* ═══════════ FTP SETTINGS ═══════════ */}
          <SectionHeader icon="fa-server" title="FTP / Connection Settings" color="#8b5cf6"
            description="Configure FTP server connection for data transfer" />
          <Row>
            <Col md={3}>
              <SettingCard name="connection_type" label="Connection Type" icon="fa-wifi"
                options={[{ label: "FTP", value: "1" }, { label: "HTTP", value: "2" }]} />
            </Col>
            <Col md={3}><TextCard name="ftp_url" label="FTP URL" placeholder="ftp://example.com" icon="fa-link" /></Col>
            <Col md={3}><TextCard name="ftp_username" label="FTP Username" placeholder="Username" icon="fa-user-lock" /></Col>
            <Col md={3}><TextCard name="ftp_password" label="FTP Password" placeholder="Password" type="password" icon="fa-key" /></Col>
          </Row>

          {/* ═══════════ SCALE SETTINGS ═══════════ */}
          <SectionHeader icon="fa-tachometer-alt" title="Scale & Analyzer Settings" color="#ef4444"
            description="Weighing scale and milk analyzer communication settings" />
          <Row>
            <Col md={4}><TextCard name="weighing_scale_baud_rate" label="Weighing Scale Baud Rate" placeholder="e.g., 9600" icon="fa-tachometer-alt" /></Col>
            <Col md={4}><TextCard name="analyzer_baud_rate" label="Analyzer Baud Rate" placeholder="e.g., 9600" icon="fa-tachometer-alt" /></Col>
          </Row>

          {/* ═══════════ SESSION SETTINGS ═══════════ */}
          <SectionHeader icon="fa-clock" title="Session Management" color="#14b8a6"
            description="Configure morning/evening session times and auto-close behavior" />
          <Row>
            <Col md={3}><TextCard name="morning_session_start" label="Morning Start Time" placeholder="HH:MM" icon="fa-sunrise" /></Col>
            <Col md={3}><TextCard name="morning_session_end" label="Morning End Time" placeholder="HH:MM" icon="fa-sun" /></Col>
            <Col md={3}><TextCard name="evening_session_start" label="Evening Start Time" placeholder="HH:MM" icon="fa-cloud-sun" /></Col>
            <Col md={3}><TextCard name="evening_session_end" label="Evening End Time" placeholder="HH:MM" icon="fa-moon" /></Col>
          </Row>
          <Row>
            <Col md={4}><SettingCard name="auto_close_session" label="Auto Close Session" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="session_summary_print" label="Print Session Summary" options={yesNo} /></Col>
          </Row>

          {/* ═══════════ QUALITY CONTROL ═══════════ */}
          <SectionHeader icon="fa-shield-alt" title="Quality Control" color="#dc2626"
            description="Set FAT/SNF thresholds to flag adulteration and quality issues" />
          <Row>
            <Col md={3}><TextCard name="min_fat_threshold" label="Min FAT Threshold" placeholder="e.g. 2.5" icon="fa-arrow-down" /></Col>
            <Col md={3}><TextCard name="max_fat_threshold" label="Max FAT Threshold" placeholder="e.g. 10.0" icon="fa-arrow-up" /></Col>
            <Col md={3}><TextCard name="min_snf_threshold" label="Min SNF Threshold" placeholder="e.g. 7.0" icon="fa-arrow-down" /></Col>
            <Col md={3}><TextCard name="max_snf_threshold" label="Max SNF Threshold" placeholder="e.g. 11.0" icon="fa-arrow-up" /></Col>
          </Row>
          <Row>
            <Col md={4}><SettingCard name="adulteration_alert" label="Adulteration Alert" icon="fa-exclamation-triangle" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="temperature_recording" label="Temperature Recording" icon="fa-thermometer-half" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="alcohol_test_required" label="Alcohol Test Required" icon="fa-flask" options={yesNo} /></Col>
          </Row>

          {/* ═══════════ PAYMENT & INCENTIVE ═══════════ */}
          <SectionHeader icon="fa-rupee-sign" title="Payment & Incentive" color="#d97706"
            description="Configure receipt content: incentives, rates, bonus, and payment slips" />
          <Row>
            <Col md={4}><SettingCard name="show_incentive_on_receipt" label="Show Incentive on Receipt" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="show_bonus_chart" label="Show Bonus Chart" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="payment_slip_print" label="Payment Slip Print" options={yesNo} /></Col>
          </Row>
          <Row>
            <Col md={4}><SettingCard name="show_rate_on_receipt" label="Show Rate on Receipt" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="show_cumulative_total" label="Show Cumulative Total" options={yesNo} /></Col>
          </Row>

          {/* ═══════════ DATA & SYNC ═══════════ */}
          <SectionHeader icon="fa-sync-alt" title="Data & Sync" color="#0891b2"
            description="Data synchronization, backup, and offline mode configuration" />
          <Row>
            <Col md={4}><TextCard name="data_sync_interval" label="Sync Interval (minutes)" placeholder="e.g. 30" icon="fa-clock" /></Col>
            <Col md={4}><SettingCard name="auto_sync_on_export" label="Auto Sync on Export" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="offline_mode" label="Offline Mode" options={yesNo} icon="fa-wifi-slash" /></Col>
          </Row>
          <Row>
            <Col md={4}><TextCard name="data_retention_days" label="Data Retention (days)" placeholder="e.g. 90" icon="fa-database" /></Col>
            <Col md={4}><SettingCard name="auto_backup" label="Auto Backup" options={yesNo} icon="fa-hdd" /></Col>
          </Row>

          {/* ═══════════ DISPLAY & DEVICE ═══════════ */}
          <SectionHeader icon="fa-mobile-alt" title="Display & Device Security" color="#7c3aed"
            description="App appearance, language, and security settings" />
          <Row>
            <Col md={4}>
              <SettingCard name="app_language" label="App Language" icon="fa-language"
                options={[
                  { label: "English", value: "en" }, { label: "Hindi", value: "hi" },
                  { label: "Tamil", value: "ta" }, { label: "Telugu", value: "te" },
                  { label: "Kannada", value: "kn" }, { label: "Marathi", value: "mr" },
                  { label: "Gujarati", value: "gu" },
                ]} />
            </Col>
            <Col md={4}><SettingCard name="dark_mode" label="Dark Mode" icon="fa-moon" options={yesNo} /></Col>
            <Col md={4}>
              <SettingCard name="font_size" label="Font Size" icon="fa-text-height"
                options={[{ label: "Small", value: "1" }, { label: "Normal", value: "2" }, { label: "Large", value: "3" }]} />
            </Col>
          </Row>
          <Row>
            <Col md={4}><SettingCard name="duplicate_entry_alert" label="Duplicate Entry Alert" icon="fa-copy" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="gps_tracking" label="GPS Tracking" icon="fa-map-marker-alt" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="app_pin_lock" label="App PIN Lock" icon="fa-lock" options={yesNo} /></Col>
          </Row>
          <Row>
            <Col md={4}><TextCard name="app_pin_code" label="App PIN Code" placeholder="Enter 4-6 digit PIN" type="password" icon="fa-key" /></Col>
          </Row>

          {/* ═══════════ APP VISIBILITY CONTROLS ═══════════ */}
          <SectionHeader icon="fa-eye" title="App Visibility Controls" color="#0891b2"
            description="Control what features and screens are visible to users in the mobile app" />
          <Row>
            <Col md={4}><SettingCard name="cow_incentive_type" label="Cow Milk Incentive" icon="fa-award"
              options={[{ label: "Not Available", value: "1" }, { label: "Common for All", value: "2" }, { label: "Producerwise", value: "3" }]} /></Col>
            <Col md={4}><SettingCard name="buffalo_incentive_type" label="Buffalo Milk Incentive" icon="fa-award"
              options={[{ label: "Not Available", value: "1" }, { label: "Common for All", value: "2" }, { label: "Producerwise", value: "3" }]} /></Col>
            <Col md={4}><SettingCard name="lock_app_with_pin" label="Lock App with PIN" options={yesNo} /></Col>
          </Row>
          <Row>
            <Col md={4}><SettingCard name="display_bulk_summary" label="Display Bulk Summary" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="display_server_producer_bills" label="Display Server Producer Bills" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="allow_local_reports" label="Allow Local Reports (Offline)" options={yesNo} /></Col>
          </Row>
          <Row>
            <Col md={4}><SettingCard name="allow_online_reports" label="Allow Online Reports" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="allow_bonus_bills" label="Allow View Bonus Bills" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="allow_rate_chart_view" label="Allow Rate Chart View" options={yesNo} /></Col>
          </Row>
          <Row>
            <Col md={4}><SettingCard name="notify_rate_chart_update" label="Notify Rate Chart Update" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="allow_incentive_view" label="Allow Incentive View" options={yesNo} /></Col>
            <Col md={4}><SettingCard name="allow_check_rate" label="Allow Check Rate in App" options={yesNo} /></Col>
          </Row>
          <Row>
            <Col md={4}><SettingCard name="use_local_rate_chart" label="Use Local Rate Chart" options={yesNo} /></Col>
          </Row>

        </AvForm>
      </div>

      {resetConfirm && (
        <SweetAlert title="Reset All Settings?" warning showCancel confirmBtnBsStyle="danger" cancelBtnBsStyle="secondary"
          onConfirm={() => { handleReset(); setResetConfirm(false) }}
          onCancel={() => setResetConfirm(false)}>
          This will reset ALL settings to factory defaults. This cannot be undone!
        </SweetAlert>
      )}
    </div>
  )
}

export default VlccMobileSettings
